import axios from 'axios';

/**
 * In development, use same-origin `/api` so the webpack dev server (craco) can proxy to Laravel.
 * That avoids requests accidentally going to `localhost:3000/api` without a backend and returning 404.
 * In production, set REACT_APP_BACKEND_URL to your API origin (e.g. https://api.example.com).
 */
const isDev = process.env.NODE_ENV === 'development';
const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = isDev
  ? '/api'
  : rawBackend
    ? `${rawBackend}/api`
    : '/api';

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('protego_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const accountId = localStorage.getItem('protego_account_id');
  const profileId = localStorage.getItem('protego_profile_id');
  if (accountId) config.headers['X-Account-Id'] = accountId;
  if (profileId) config.headers['X-Profile-Id'] = profileId;
  config.timeout = config.timeout ?? 30000;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/signup')) {
        localStorage.removeItem('protego_token');
        localStorage.removeItem('protego_user');
        localStorage.removeItem('protego_account_id');
        localStorage.removeItem('protego_profile_id');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  signup: (data) => api.post('/auth/signup', data),
  me: () => api.get('/auth/user'),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const sellersAPI = {
  getAll: (params) => api.get('/sellers', { params }),
  getOverview: (id, params) => api.get(`/sellers/${id}/overview`, { params }),
  getMetrics: (id, { category, dateFrom, dateTo } = {}) => api.get(`/sellers/${id}/metrics`, { params: { category, date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  getAlerts: (id, severity) => api.get(`/sellers/${id}/alerts`, { params: { severity } }),
  getSignals: (id, severity, category) => api.get(`/sellers/${id}/signals`, { params: { severity, category } }),
  getHistory: (id) => api.get(`/sellers/${id}/history`),
  getPreemptiveActions: (id) => api.get(`/sellers/${id}/preemptive-actions`),
  sync: (id, data) => api.post(`/sellers/${id}/sync`, data),
  syncByProfile: (data) => api.post('/sellers/sync-profile', data),
  getSignalOrders: (sellerId, signalId) => api.get(`/sellers/${sellerId}/signals/${signalId}/orders`),
  getAsinRisks: (id) => api.get(`/sellers/${id}/asin-risks`),
  getTasks: (id, status) => api.get(`/sellers/${id}/tasks`, { params: { status } }),
  createTask: (id, data) => api.post(`/sellers/${id}/tasks`, data),
  updateTask: (sellerId, taskId, data) => api.patch(`/sellers/${sellerId}/tasks/${taskId}`, data),
  getAuditReport: (id) => api.get(`/sellers/${id}/audit-report`),
  downloadAuditReportPdf: (id, params = {}) => api.get(`/sellers/${id}/audit-report/pdf`, {
    responseType: 'blob',
    params: { template: 'light', ...params },
  }),
};

export const adminAPI = {
  getSellers: () => api.get('/admin/sellers'),
  getStats: () => api.get('/admin/stats'),
  bulkSync: () => api.post('/admin/bulk-sync'),
};

export const poaAPI = {
  getTemplates: () => api.get('/poa-templates'),
  getTemplate: (metricName) => api.get(`/poa-templates/${encodeURIComponent(metricName)}`),
};

export const poaSubmissionsAPI = {
  me: () => api.get('/poa-submissions/me'),
  prefill: (accountId) => api.get('/poa-submissions/prefill', { params: { account_id: accountId } }),
  saveDraft: (data) => api.put('/poa-submissions', data),
  parse: (data) => api.post('/poa-submissions/parse', data, { timeout: 90000 }),
  submit: (data) => api.post('/poa-submissions/submit', data),
  aiCoachSection: (data) => api.post('/poa-submissions/ai/coach-section', data, { timeout: 90000 }),
  aiDraftSection: (data) => api.post('/poa-submissions/ai/draft-section', data, { timeout: 90000 }),
  aiChat: (data) => api.post('/poa-submissions/ai/chat', data, { timeout: 90000 }),
  aiDocumentRequirements: (data) => api.post('/poa-submissions/ai/document-requirements', data, { timeout: 90000 }),
  aiValidateReview: (data) => api.post('/poa-submissions/ai/validate-review', data, { timeout: 30000 }),
  aiGenerateDocument: (data) => api.post('/poa-submissions/ai/generate-document', data, { timeout: 180000 }),
};

export const settingsAPI = {
  getProfile: () => api.get('/settings/profile'),
  getBilling: () => api.get('/settings/billing'),
  confirmCheckout: (sessionId) => api.post('/settings/confirm-checkout', { session_id: sessionId }),
  createCheckoutSession: (data) => api.post('/settings/checkout-session', data),
  syncPayments: () => api.post('/settings/sync-payments'),
  updateProfile: (data) => api.put('/settings/profile', data),
  changePassword: (data) => api.put('/settings/password', data),
  sendTestEmail: (data) => api.post('/settings/test-email', data),
  bookingConfirmed: (data) => api.post('/settings/booking-confirmed', data),
};

export const meetingAgentAPI = {
  googleConnect: () => api.post('/meeting-agent/google/connect'),
  googleStatus: () => api.get('/meeting-agent/google/status'),
  googleWatch: () => api.post('/meeting-agent/google/watch'),
  googleSync: () => api.post('/meeting-agent/google/sync'),
  listMeetings: () => api.get('/meeting-agent/meetings'),
  getMeeting: (id) => api.get(`/meeting-agent/meetings/${id}`),
  createInstant: (data) => api.post('/meeting-agent/meetings/instant', data, { timeout: 180000 }),
  downloadPdf: (id) => api.get(`/meeting-agent/meetings/${id}/pdf`, { responseType: 'blob' }),
};

export const emailSequencesAPI = {
  list: () => api.get('/admin/email-sequences'),
  get: (key) => api.get(`/admin/email-sequences/${key}`),
  update: (key, data) => api.put(`/admin/email-sequences/${key}`, data),
  reset: (key) => api.delete(`/admin/email-sequences/${key}`),
  preview: (key, data) => api.post(`/admin/email-sequences/${key}/preview`, data),
  sendTest: (key, data) => api.post(`/admin/email-sequences/${key}/send-test`, data),
};

export const usersAPI = {
  list: (params) => api.get('/admin/users', { params }),
  get: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  remove: (id) => api.delete(`/admin/users/${id}`),
  sendInvite: (id) => api.post(`/admin/users/${id}/send-invite`),
  updateProfiles: (id, profileIds) => api.put(`/admin/users/${id}/profiles`, { profile_ids: profileIds }),
};

export const profilesAPI = {
  listAll: (params) => api.get('/profiles/all/list', { params }),
};

export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  getMarketplaces: () => api.get('/marketplaces'),
  startSpConnect: (data) => api.post('/sp/connect', data),
  getProfiles: (accountId) => api.get(`/accounts/${accountId}/profiles`),
  updateSupportAccess: (id, payload) => api.post(`/accounts/${id}/support-access`, payload),
};

export const amazonSupportAPI = {
  getPolicyViolations: (accountId, { dateFrom, dateTo } = {}) => api.get(`/accounts/${accountId}/policy-violations`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  getVoiceOfCustomer: (accountId, { dateFrom, dateTo } = {}) => api.get(`/accounts/${accountId}/voice-of-customer`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  getPerformanceNotifications: (accountId, { dateFrom, dateTo } = {}) => api.get(`/accounts/${accountId}/performance-notifications`, { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } }),
  triggerScrape: (accountId) => api.post(`/accounts/${accountId}/trigger-scrape`),
  getScrapeLogs: (accountId, lines = 200) => api.get(`/accounts/${accountId}/scrape-logs`, { params: { lines, _ts: Date.now() } }),
};

export const noraAPI = {
  status: () => api.get('/nora/status'),
  chat: (data) => api.post('/nora/chat', data, { timeout: 120000 }),
};

export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
